// Detalle/editor de una política especial: identidad + palancas combinables, alcance por dimensión,
// vigencia, historial y menú de acciones. El tipo primario se deduce de las palancas (suspender
// exclusivo · 1 = puro · 2+ = combo) y las acciones, del estado que devuelve el backend.
import { useState } from 'react';
import { Clock, Pause, Play, Rocket, Trash2 } from 'lucide-react';
import { ActionMenu, Button } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { ValidityCalendars } from '@/shared/patterns/ValidityCalendars';
import type { ScopeOption } from '../../promotions/api/useScopeCatalog';
import type { SegmentCatalogState } from '../../segments/types';
import { PolicyHistory } from './PolicyHistory';
import { ScopeChips } from './ScopeChips';
import { DiscardX, FIELD_INPUT, FIELD_LABEL, SecTitle, StatusBadge, TypeBadge } from './ui';
import {
    actionCopy,
    allowedActions,
    canEditPolicy,
    derivePolicyType,
    describeScope,
    policyView,
} from '../logic';
import {
    EASABLE_LEVERS,
    POLICY_LEVER_LABEL,
    POLICY_LEVER_UNIT,
    type PolicyAction,
    type PolicyDraft,
    type PolicyLeverDirection,
    type PolicyLeverKey,
    type PolicyLevers,
    type Segment,
    type SpecialPolicy,
    type StorePolicyLogEntry,
} from '../types';

const LEVER_ORDER: readonly PolicyLeverKey[] = ['sciMin', 'downMinPct', 'tna', 'maxAmount'];

const ACTION_ICON: Record<PolicyAction, React.ReactNode> = {
    publish: <Rocket className="size-3" aria-hidden />,
    pause: <Pause className="size-3" aria-hidden />,
    resume: <Play className="size-3" aria-hidden />,
    expire: <Clock className="size-3" aria-hidden />,
    archive: <Trash2 className="size-3" aria-hidden />,
};

/** Botón chico que elige si la palanca endurece o afloja. */
function DirectionToggle({
    value,
    disabled,
    onChange,
}: {
    value: PolicyLeverDirection;
    disabled: boolean;
    onChange: (direction: PolicyLeverDirection) => void;
}) {
    const option = (direction: PolicyLeverDirection, label: string): React.ReactNode => (
        <button
            type="button"
            disabled={disabled}
            aria-pressed={value === direction}
            onClick={() => {
                onChange(direction);
            }}
            className={cn(
                'rounded-[3px] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide transition-colors disabled:opacity-50',
                value === direction
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
        >
            {label}
        </button>
    );
    return (
        <span className="inline-flex gap-0.5">
            {option('harden', 'Endurece')}
            {option('ease', 'Afloja')}
        </span>
    );
}

/** Input numérico que admite vacío (palanca apagada = clave ausente). */
function LeverInput({
    leverKey,
    lever,
    disabled,
    onValueChange,
    onDirectionChange,
}: {
    leverKey: PolicyLeverKey;
    lever: PolicyLevers[PolicyLeverKey];
    disabled: boolean;
    onValueChange: (value: number | undefined) => void;
    onDirectionChange: (direction: PolicyLeverDirection) => void;
}) {
    const [draft, setDraft] = useState<string | null>(null);
    const label = POLICY_LEVER_LABEL[leverKey];
    return (
        <div className="grid grid-cols-[120px_1fr_28px] items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                {label}
            </span>
            <input
                type="number"
                placeholder="—"
                aria-label={label}
                disabled={disabled}
                value={draft ?? (lever === undefined ? '' : String(lever.value))}
                onChange={(e) => {
                    const raw = e.target.value;
                    setDraft(raw);
                    if (raw === '') {
                        onValueChange(undefined);
                        return;
                    }
                    const num = Number(raw);
                    if (Number.isFinite(num)) onValueChange(num);
                }}
                onBlur={() => {
                    setDraft(null);
                }}
                className="rounded border border-border bg-background px-2 py-1 text-xs tabular-nums outline-none focus:border-ring disabled:opacity-50"
            />
            <span className="text-[11px] text-muted-foreground">{POLICY_LEVER_UNIT[leverKey]}</span>
            {lever !== undefined && EASABLE_LEVERS.includes(leverKey) ? (
                <div className="col-start-2 -mt-1">
                    <DirectionToggle
                        value={lever.direction}
                        disabled={disabled}
                        onChange={onDirectionChange}
                    />
                </div>
            ) : null}
        </div>
    );
}

export function PolicyDetail({
    policy,
    draft,
    isNew,
    changeCount,
    saving,
    canSave,
    segments,
    stores,
    products,
    segmentCatalogState,
    storesLoading,
    productsLoading,
    today,
    logEntries,
    logLoading,
    onDraftChange,
    onSave,
    onDiscard,
    onAction,
}: {
    /** La política persistida; `null` mientras se redacta una nueva. */
    policy: SpecialPolicy | null;
    draft: PolicyDraft;
    isNew: boolean;
    changeCount: number;
    saving: boolean;
    canSave: boolean;
    segments: readonly Segment[];
    stores: readonly ScopeOption[];
    products: readonly ScopeOption[];
    segmentCatalogState: SegmentCatalogState;
    storesLoading: boolean;
    productsLoading: boolean;
    today: string;
    logEntries: readonly StorePolicyLogEntry[];
    logLoading: boolean;
    onDraftChange: (draft: PolicyDraft) => void;
    onSave: () => void;
    onDiscard: () => void;
    onAction: (action: PolicyAction) => void;
}) {
    const type = derivePolicyType(draft);
    const view = policy === null ? 'DRAFT' : policyView(policy);
    const locked = policy !== null && !canEditPolicy(policy.status);
    const readOnly = locked || !canSave;

    const setLever = (key: PolicyLeverKey, value: number | undefined): void => {
        // Apagar una palanca la SACA del objeto: una clave presente en `undefined` viajaría igual
        // al wire y el backend la leería como palanca prendida sin valor.
        const levers = Object.fromEntries(
            Object.entries(draft.levers).filter(([leverKey]) => leverKey !== key),
        ) as PolicyLevers;
        if (value !== undefined) {
            levers[key] = { value, direction: draft.levers[key]?.direction ?? 'harden' };
        }
        onDraftChange({ ...draft, levers });
    };

    const setDirection = (key: PolicyLeverKey, direction: PolicyLeverDirection): void => {
        const current = draft.levers[key];
        if (current === undefined) return;
        onDraftChange({ ...draft, levers: { ...draft.levers, [key]: { ...current, direction } } });
    };

    const scopeParts = describeScope(draft);
    const menuItems = allowedActions(view).map((action) => ({
        label: actionCopy(action).label,
        icon: ACTION_ICON[action],
        destructive: actionCopy(action).destructive,
        onSelect: () => {
            onAction(action);
        },
    }));

    return (
        <>
            {/* .detail-head */}
            <div className="flex flex-none items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-bold tracking-tight">
                        {draft.name || 'Nueva política'}
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                        {isNew
                            ? 'Borrador nuevo · se crea sin publicar'
                            : `Versión ${String(policy?.version ?? 0)}`}
                    </div>
                </div>
                <div className="flex flex-none items-center gap-1.5">
                    <TypeBadge type={type} />
                    <StatusBadge status={view} />
                    {changeCount > 0 && !readOnly ? (
                        <>
                            <Button
                                size="sm"
                                className="h-6 px-2.5 text-[11px]"
                                disabled={saving}
                                onClick={onSave}
                            >
                                {isNew ? 'Crear política' : `Guardar (${String(changeCount)})`}
                            </Button>
                            <DiscardX onClick={onDiscard} />
                        </>
                    ) : null}
                    {menuItems.length > 0 && !isNew && canSave ? (
                        <ActionMenu items={menuItems} />
                    ) : null}
                </div>
            </div>

            {/* .detail-body */}
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3.5 py-2.5">
                {locked ? (
                    <div
                        role="status"
                        className="mb-2.5 rounded-md border border-border bg-muted/40 px-3 py-2 text-[10.5px] text-muted-foreground"
                    >
                        Esta política está vencida. Vencer es definitivo: no se puede reanudar ni
                        volver a editar.
                    </div>
                ) : null}

                {/* Identidad + palancas combinables */}
                <div className="mb-2.5 grid grid-cols-2 gap-3 border-b border-border/50 pb-2.5">
                    <div className="space-y-2.5">
                        <div>
                            <label className={FIELD_LABEL} htmlFor="pol-name">
                                Nombre
                            </label>
                            <input
                                id="pol-name"
                                type="text"
                                value={draft.name}
                                disabled={readOnly}
                                placeholder="ej. Nezahualcóyotl · enganche mínimo más alto"
                                onChange={(e) => {
                                    onDraftChange({ ...draft, name: e.target.value });
                                }}
                                className={FIELD_INPUT}
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL} htmlFor="pol-description">
                                Descripción / motivo
                            </label>
                            <textarea
                                id="pol-description"
                                rows={3}
                                value={draft.description}
                                disabled={readOnly}
                                placeholder="Por qué se está aplicando · qué disparó la decisión · qué se espera al vencerla"
                                onChange={(e) => {
                                    onDraftChange({ ...draft, description: e.target.value });
                                }}
                                className={FIELD_INPUT}
                            />
                        </div>
                    </div>

                    <div className="rounded-md border-l-[3px] border-primary bg-muted/30 px-3 py-2.5">
                        <label
                            aria-label="Suspender créditos nuevos"
                            className={cn(
                                'mb-2 flex cursor-pointer items-start gap-2 rounded-[5px] border px-2.5 py-2 transition-colors',
                                draft.suspend
                                    ? 'border-destructive/40 bg-destructive/10'
                                    : 'border-border bg-card hover:bg-accent/40',
                            )}
                        >
                            <input
                                type="checkbox"
                                checked={draft.suspend}
                                disabled={readOnly}
                                onChange={(e) => {
                                    onDraftChange({ ...draft, suspend: e.target.checked });
                                }}
                                className="mt-0.5 accent-destructive"
                            />
                            <span>
                                <span className="block text-xs font-semibold">
                                    Suspender créditos nuevos
                                </span>
                                <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground">
                                    Bloquea originación en el alcance. Al activarlo se ignoran las
                                    otras palancas.
                                </span>
                            </span>
                        </label>
                        <div
                            className={cn(
                                'flex flex-col gap-1.5',
                                draft.suspend && 'pointer-events-none opacity-40',
                            )}
                        >
                            {LEVER_ORDER.map((key) => (
                                <LeverInput
                                    key={key}
                                    leverKey={key}
                                    lever={draft.levers[key]}
                                    disabled={readOnly}
                                    onValueChange={(value) => {
                                        setLever(key, value);
                                    }}
                                    onDirectionChange={(direction) => {
                                        setDirection(key, direction);
                                    }}
                                />
                            ))}
                        </div>
                        <div className="mt-1.5 text-[10px] leading-snug text-muted-foreground">
                            Llena las palancas que aplican y elige si endurecen o aflojan el plan.
                            El tipo se deduce automáticamente; con 2 o más se marca como Combo. El
                            SCI solo endurece.
                        </div>
                    </div>
                </div>

                {/* Alcance */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <SecTitle>Alcance</SecTitle>
                    <ScopeChips
                        segments={segments}
                        stores={stores}
                        products={products}
                        segmentCatalogState={segmentCatalogState}
                        storesLoading={storesLoading}
                        productsLoading={productsLoading}
                        targetLevels={draft.targetLevels}
                        storeIds={draft.storeIds}
                        productSkus={draft.productSkus}
                        segmentIds={draft.segmentIds}
                        disabled={readOnly}
                        onChange={(patch) => {
                            onDraftChange({ ...draft, ...patch });
                        }}
                    />
                    <div className="mt-1.5 text-[10.5px] tabular-nums text-muted-foreground">
                        Aplica a{' '}
                        {scopeParts.map((part, i) => (
                            <span key={part}>
                                <b className="font-semibold text-foreground">{part}</b>
                                {i < scopeParts.length - 1 ? ' · ' : ''}
                            </span>
                        ))}
                    </div>
                    {draft.allowedSkus.length > 0 ? (
                        <div className="mt-1 text-[10.5px] text-muted-foreground">
                            Compuerta de SKU: solo se financian{' '}
                            <b className="font-semibold text-foreground">
                                {String(draft.allowedSkus.length)}
                            </b>{' '}
                            productos. Esta pantalla todavía no edita esa lista; se conserva tal
                            como está guardada.
                        </div>
                    ) : null}
                </div>

                {/* Vigencia */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <SecTitle>Vigencia</SecTitle>
                    <ValidityCalendars
                        validFrom={draft.validFrom}
                        validTo={draft.validTo}
                        today={today}
                        disabled={readOnly}
                        onChange={(validity) => {
                            onDraftChange({ ...draft, ...validity });
                        }}
                    />
                </div>

                {/* Historial */}
                <div>
                    <SecTitle>Historial de esta política</SecTitle>
                    <PolicyHistory
                        entries={logEntries}
                        isLoading={logLoading}
                        emptyMessage={
                            isNew
                                ? 'La política todavía no existe: su historial arranca al crearla.'
                                : 'Sin eventos registrados'
                        }
                    />
                    {policy !== null && policy.createdBy !== '' ? (
                        <div className="mt-1 text-[10px] text-muted-foreground">
                            Creada por {policy.createdBy}
                            {policy.validFrom ? ` · arranca ${formatDateMx(policy.validFrom)}` : ''}
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
}
